import { useState, useRef, useEffect, useCallback } from 'react';
import I18n from 'I18n';
import debounce from 'transmute/debounce';
import { getInvoiceProperty, getInvoiceAssociatedObjectProperty } from 'invoices-ui-lib/utils/property';
import { fetchEmailContactOptions } from 'invoices-ui-lib/api/CrmSearchAPI';
import { getOptions, getOptionsFromContacts } from 'invoices-ui-lib/components/InvoiceSendEmailModal/InvoiceSendEmailModal';
import { isValidEmail } from 'invoices-ui-lib/utils/validation';
import RecordPaymentTabs from './RecordPaymentTabs';
import PortalIdParser from 'PortalIdParser';
import { COMMERCE_PAYMENT_TYPE_ID, CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { createCommercePaymentObject } from '../../api/paymentsAPI';

// @ts-expect-error not typed
import { fromMoment, toMoment } from 'UIComponents/core/SimpleDate';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import UICurrencyInput from 'UIComponents/input/UICurrencyInput';
import UISelect from 'UIComponents/input/UISelect';
import UITextArea from 'UIComponents/input/UITextArea';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UICheckbox from 'UIComponents/input/UICheckbox';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DEBOUNCE_DELAY = 300;
const REFERENCE_NUMBER_MAX_CHARACTER_LIMIT = 50;
const validReferenceNumberRegExp = new RegExp('^[a-zA-Z0-9_/;:(). &?!\\\\-]*$');
const getInitialPaymentRecord = invoice => ({
  associatedObjects: {
    COMMERCE_PAYMENT_TO_INVOICE: [invoice],
    COMMERCE_PAYMENT_TO_CONTACT: invoice.associatedObjects.INVOICE_TO_CONTACT[0] ? [invoice.associatedObjects.INVOICE_TO_CONTACT[0]] : []
  },
  objectTypeId: COMMERCE_PAYMENT_TYPE_ID,
  portalId: PortalIdParser.get(),
  objectId: null,
  properties: {
    hs_initial_amount: {
      name: 'hs_initial_amount',
      value: getInvoiceProperty(invoice, 'hs_balance_due')
    },
    hs_initiated_date: {
      name: 'hs_initiated_date',
      value: I18n.moment.userTz().startOf('day').valueOf()
    },
    hs_payment_method_type: {
      name: 'hs_payment_method_type',
      value: ''
    },
    hs_reference_number: {
      name: 'hs_reference_number',
      value: ''
    },
    hs_internal_comment: {
      name: 'hs_internal_comment',
      value: ''
    }
  }
});
const formatContactDisplayName = ({
  name,
  email
}) => name && email ? `${email} (${name})` : email ? `${email}` : `(${name})`;
const getReferenceNumberValidationMessage = referenceNumberValue => {
  if (!referenceNumberValue) return null;
  if (!validReferenceNumberRegExp.test(referenceNumberValue)) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoicesHighlightCard.modals.recordPayment.referenceNumber.validation.invalid"
    });
  } else if (referenceNumberValue.length > REFERENCE_NUMBER_MAX_CHARACTER_LIMIT) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoicesHighlightCard.modals.recordPayment.referenceNumber.validation.overMaxLength"
    });
  }
  return null;
};
const getPaymentAmountValidationMessage = (paymentAmount, invoice) => {
  if (!paymentAmount || +paymentAmount > +getInvoiceProperty(invoice, 'hs_balance_due')) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "sHighlightCard.modals.recordPayment.amount.validationMessage"
    });
  }
  return null;
};
const getContactEmailValidationMessage = paymentAssociatedContact => {
  if (!paymentAssociatedContact) return null;
  return isValidEmail(getInvoiceAssociatedObjectProperty(paymentAssociatedContact, 'email')) ? null : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoicesHighlightCard.modals.recordPayment.customerEmail.validation.invalidEmail"
  });
};
const RecordNewPaymentTab = props => {
  const {
    onSuccess,
    onFailure,
    onReject,
    invoice,
    selectedTab,
    setSelectedTab,
    paymentMethodOptions
  } = props;
  const [paymentRecord, setPaymentRecord] = useState(getInitialPaymentRecord(invoice));
  const [contacts, setContacts] = useState(invoice.associatedObjects.INVOICE_TO_CONTACT[0] ? [invoice.associatedObjects.INVOICE_TO_CONTACT[0]] : []);
  const [referenceNumberError, setReferenceNumberError] = useState(false);
  const [paymentAmountError, setPaymentAmountError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldSendReceipt, setShouldSendReceipt] = useState(true);
  const currentDate = I18n.moment.userTz();
  const oneYearAgo = I18n.moment(currentDate).subtract(1, 'year');
  const currency = getInvoiceProperty(invoice, 'hs_currency');
  const paymentDate = paymentRecord.properties.hs_initiated_date.value;
  const paymentAssociatedContact = paymentRecord.associatedObjects.COMMERCE_PAYMENT_TO_CONTACT[0];
  const referenceNumber = paymentRecord.properties.hs_reference_number.value;
  const paymentAmount = paymentRecord.properties.hs_initial_amount.value;
  const lastEditDate = getInvoiceProperty(invoice, 'hs_last_edit_date');
  const debouncedReferenceNumberCheck = useRef(debounce(DEBOUNCE_DELAY, newReferenceNumber => {
    setReferenceNumberError(!!getReferenceNumberValidationMessage(newReferenceNumber));
  })).current;
  const debouncedPaymentAmountCheck = useRef(debounce(DEBOUNCE_DELAY, newPaymentAmount => {
    setPaymentAmountError(!!getPaymentAmountValidationMessage(newPaymentAmount, invoice));
  })).current;
  useEffect(() => {
    debouncedReferenceNumberCheck(referenceNumber);
  }, [referenceNumber, debouncedReferenceNumberCheck]);
  useEffect(() => {
    debouncedPaymentAmountCheck(paymentAmount);
  }, [paymentAmount, debouncedPaymentAmountCheck]);
  const debouncedHandleLoadOptions = useRef(debounce(DEBOUNCE_DELAY, (searchQuery, loadOptionsCallback) => {
    if (!searchQuery.length) {
      loadOptionsCallback(null);
      return;
    }
    fetchEmailContactOptions(searchQuery).then(fetchedContacts => {
      setContacts(fetchedContacts);
      const options = getOptions(searchQuery, fetchedContacts, formatContactDisplayName);
      loadOptionsCallback(null, {
        options
      });
    })
    // This would be a failure during the user input-triggered search. On failure,
    // swallow the error quietly so the user can hopefully recover by continuing to type.
    .catch(() => {});
  })).current;
  useEffect(() => {
    if (paymentMethodOptions.length) {
      setPaymentRecord(existingPaymentRecord => Object.assign({}, existingPaymentRecord, {
        properties: Object.assign({}, existingPaymentRecord.properties, {
          hs_payment_method_type: {
            name: 'hs_payment_method_type',
            value: paymentMethodOptions.find(option => option.value === 'check') ? 'check' : paymentMethodOptions[0].value
          }
        })
      }));
    }
  }, [paymentMethodOptions]);
  const shouldDisableCreate = isLoading || !paymentRecord.properties.hs_initiated_date.value || !paymentAssociatedContact || !isValidEmail(getInvoiceAssociatedObjectProperty(paymentAssociatedContact, 'email')) || referenceNumberError || !paymentRecord.properties.hs_payment_method_type.value || paymentAmountError;
  const handleChangeContactAssociation = useCallback(event => {
    const newSelectedContact = contacts.find(contactOption => contactOption.objectId === event.target.value) || {
      objectId: null,
      objectTypeId: CONTACT_TYPE_ID,
      properties: {
        email: {
          name: 'email',
          value: event.target.value
        }
      },
      portalId: PortalIdParser.get()
    };
    setPaymentRecord(Object.assign({}, paymentRecord, {
      associatedObjects: Object.assign({}, paymentRecord.associatedObjects, {
        COMMERCE_PAYMENT_TO_CONTACT: [newSelectedContact]
      })
    }));
  }, [contacts, paymentRecord]);
  const handleOnClick = useCallback(() => {
    setIsLoading(true);
    createCommercePaymentObject({
      commercePayment: paymentRecord,
      lastEditDate,
      shouldSendReceipt
    }).then(onSuccess).catch(onFailure);
  }, [onFailure, onSuccess, paymentRecord, setIsLoading, lastEditDate, shouldSendReceipt]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(UIDialogBody, {
      children: [/*#__PURE__*/_jsx(RecordPaymentTabs, {
        selectedTab: selectedTab,
        setSelectedTab: setSelectedTab,
        shouldDisableTabs: isLoading
      }), /*#__PURE__*/_jsx("div", {
        style: {
          fontWeight: 500
        },
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.balanceDue_jsx",
          elements: {
            FormattedCurrency
          },
          options: {
            currencyCode: currency,
            value: +getInvoiceProperty(invoice, 'hs_balance_due')
          }
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        className: "p-top-4",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.amount.label"
        }),
        validationMessage: paymentAmountError && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.amount.validationMessage"
        }),
        error: paymentAmountError,
        children: /*#__PURE__*/_jsx(UICurrencyInput, {
          "data-test-id": "record-payment-panel-amount-field",
          value: +paymentRecord.properties.hs_initial_amount.value,
          currency: currency,
          onChange: ({
            target: {
              value
            }
          }) => setPaymentRecord(Object.assign({}, paymentRecord, {
            properties: Object.assign({}, paymentRecord.properties, {
              hs_initial_amount: {
                name: 'hs_initial_amount',
                value
              }
            })
          })),
          min: 0
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.paymentDate.label"
        }),
        children: /*#__PURE__*/_jsx(UIDateInput, {
          value: paymentDate ? fromMoment(I18n.moment(paymentDate)) : paymentDate,
          min: fromMoment(oneYearAgo),
          max: fromMoment(currentDate),
          onChange: ({
            target: {
              value
            }
          }) => setPaymentRecord(Object.assign({}, paymentRecord, {
            properties: Object.assign({}, paymentRecord.properties, {
              hs_initiated_date: {
                name: 'hs_initiated_date',
                value: value ? toMoment(value).valueOf() : value
              }
            })
          }))
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.paymentMethod.label"
        }),
        children: paymentMethodOptions.length ? /*#__PURE__*/_jsx(UISelect, {
          value: paymentRecord.properties.hs_payment_method_type.value,
          options: paymentMethodOptions,
          onChange: event => setPaymentRecord(Object.assign({}, paymentRecord, {
            properties: Object.assign({}, paymentRecord.properties, {
              hs_payment_method_type: {
                name: 'hs_payment_method_type',
                value: event.target.value
              }
            })
          }))
        }) : /*#__PURE__*/_jsx(UILoadingSpinner, {})
      }), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.referenceNumber.label"
        }),
        error: referenceNumberError,
        validationMessage: referenceNumberError && getReferenceNumberValidationMessage(referenceNumber),
        children: /*#__PURE__*/_jsx(UITextInput, {
          "data-test-id": "record-payment-panel-reference-number",
          value: referenceNumber,
          onChange: ({
            target: {
              value
            }
          }) => setPaymentRecord(Object.assign({}, paymentRecord, {
            properties: Object.assign({}, paymentRecord.properties, {
              hs_reference_number: {
                name: 'hs_reference_number',
                value
              }
            })
          })),
          placeholder: I18n.text('invoicesHighlightCard.modals.recordPayment.referenceNumber.placeholder')
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.internalNote.label"
        }),
        help: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.internalNote.helpText"
        }),
        children: /*#__PURE__*/_jsx(UITextArea, {
          value: paymentRecord.properties.hs_internal_comment.value,
          placeholder: I18n.text('invoicesHighlightCard.modals.recordPayment.internalNote.placeholder'),
          onChange: event => setPaymentRecord(Object.assign({}, paymentRecord, {
            properties: Object.assign({}, paymentRecord.properties, {
              hs_internal_comment: {
                name: 'hs_internal_comment',
                value: event.target.value
              }
            })
          }))
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.customerEmail.label"
        }),
        help: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.customerEmail.help"
        }),
        required: true,
        error: !!getContactEmailValidationMessage(paymentAssociatedContact),
        validationMessage: getContactEmailValidationMessage(paymentAssociatedContact),
        children: /*#__PURE__*/_jsx(UISelect, {
          onChange: handleChangeContactAssociation,
          loadOptions: debouncedHandleLoadOptions,
          value: paymentAssociatedContact ? paymentAssociatedContact.objectId || paymentAssociatedContact.properties.email.value : undefined,
          options: invoice.associatedObjects.INVOICE_TO_CONTACT[0] ? getOptionsFromContacts([invoice.associatedObjects.INVOICE_TO_CONTACT[0]], formatContactDisplayName) : []
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        children: /*#__PURE__*/_jsx(UICheckbox, {
          "data-test-id": "send-receipt-checkbox",
          onChange: () => setShouldSendReceipt(previousState => !previousState),
          checked: shouldSendReceipt,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.modals.recordPayment.sendEmail"
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UILoadingButton, {
        use: "primary",
        onClick: handleOnClick,
        disabled: shouldDisableCreate,
        "data-test-id": "invoice-record-payment-panel-confirm-button",
        loading: isLoading,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.confirmButton"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: onReject,
        disabled: isLoading,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.modals.recordPayment.cancelButton"
        })
      })]
    })]
  });
};
export default RecordNewPaymentTab;